import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import PreInscriptionSummaryFilters from './components/PreInscriptionSummaryFilters';
import PreInscriptionSummaryList from './components/PreInscriptionSummaryList';
import { IFilters } from './types';
import {
  downloadPreInscriptionsSummary,
  fetchPreInscriptionsSummary,
} from '../../api/pre-inscription-summary';
import ContentWrapper from '../../components/ContentWrapper';
import ProgressSection from '../../components/ProgressSection';
import { IPreInscriptionSummary } from '../../types/pre-inscription-summary.type';
import { HttpUtils } from '../../utils/http.utils';

const PreInscriptionsSummary = React.memo(() => {
  const [summary, setSummary] = useState<IPreInscriptionSummary[]>([]);
  const [isSubmitProcess, setSubmitProcess] = useState<boolean>(false);
  const [isExportProcess, setExportProcess] = useState<boolean>(false);
  const [isReady, setReady] = useState<boolean>(false);

  const { t } = useTranslation();

  const handleSubmit = React.useCallback(
    (filters: IFilters) => {
      setSubmitProcess(true);

      if (!isReady) {
        setReady(true);
      }

      fetchPreInscriptionsSummary(filters)
        .then((response) => {
          setSummary(response);
        })
        .catch(() => setSummary([]))
        .finally(() => setSubmitProcess(false));
    },
    [isReady],
  );

  const handleExport = React.useCallback(
    (filters: IFilters) => {
      setExportProcess(true);

      if (!isReady) {
        setReady(true);
      }

      downloadPreInscriptionsSummary(filters)
        .then((response) => HttpUtils.handleBlob(response, 'pre_inscriptions_summary.xlsx'))
        .finally(() => setExportProcess(false));
    },
    [isReady],
  );

  const renderList = React.useMemo(() => {
    if (!isReady) {
      return null;
    }

    if (isSubmitProcess) {
      return <ProgressSection mt={3} />;
    }

    return <PreInscriptionSummaryList summary={summary} />;
  }, [summary, isReady, isSubmitProcess]);

  return (
    <ContentWrapper title={t('pre_inscriptions_summary.title')}>
      <PreInscriptionSummaryFilters
        onSubmit={handleSubmit}
        onExport={handleExport}
        isSubmitProcess={isSubmitProcess}
        isExportProcess={isExportProcess}
      />

      {renderList}
    </ContentWrapper>
  );
});

export default PreInscriptionsSummary;
